import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import FBOMenuButton from 'ui/theme/components/FBOMenuButton/FBOMenuButton';
import { Undo, Redo, Grid, Eraser } from 'lucide-react';
import { LabelElement, PageSize, TemplateApiPayload } from '../../types';
import { colorPalette } from 'ui/theme';
import { IconNames } from 'ui/theme';
import { MenuItemVariant } from 'ui/components/Menu';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete';
import { MenuItem } from 'ui/components/Menu';
import SliderToggle from 'ui/components/SliderToggle'; // Added SliderToggle import

interface GenerateTemplateMenuItemsParams {
  currentTemplateId?: string | null;
  isCurrentTemplateDefault?: boolean;
  onSetAsDefault?: (templateId: string) => void;
  onDeleteTemplate?: (templateId: string) => void;
}

const modeOptions: {
  value: 'design' | 'preview';
  label: string;
  'data-qa': string;
}[] = [
  {
    value: 'design',
    label: 'Design',
    'data-qa': 'slider-toggle-design-mode',
  },
  {
    value: 'preview',
    label: 'Preview',
    'data-qa': 'slider-toggle-preview-mode',
  },
];

const generateTemplateMenuItems = ({
  currentTemplateId,
  isCurrentTemplateDefault,
  onSetAsDefault,
  onDeleteTemplate,
}: GenerateTemplateMenuItemsParams): MenuItem[] => [
  {
    label: 'Set as default',
    onOptionClick: () => {
      if (currentTemplateId && onSetAsDefault) {
        onSetAsDefault(currentTemplateId);
      }
    },
    disabled: !currentTemplateId || isCurrentTemplateDefault,
    dataQa: 'template-menu-set-default',
    // icon: IconNames.FBOStar, // Consider adding an icon if available
  },
  {
    label: 'Delete Template',
    onOptionClick: () => {
      if (currentTemplateId && onDeleteTemplate) {
        onDeleteTemplate(currentTemplateId);
      }
    },
    disabled: !currentTemplateId || isCurrentTemplateDefault,
    dataQa: 'template-menu-delete',
    icon: IconNames.FBODeleteSmall,
    variant: MenuItemVariant.Danger,
  },
];

interface DesignerHeaderProps {
  onDeleteElement?: () => void;
  previewMode: boolean;
  onTogglePreview: () => void;
  showGrid: boolean;
  onToggleGrid: () => void;
  selectedPageSize: PageSize;
  onPageSizeChange: (pageSize: PageSize) => void;
  pageSizes: PageSize[];
  onUndo?: () => void;
  onRedo?: () => void;
  onClearCanvas: () => void;
  onGroup: () => void;
  selectedElements: LabelElement[];
  availableTemplates: TemplateApiPayload[];
  onTemplateSelect: (selectedTemplate: TemplateApiPayload | null) => void;
  onCreateNewTemplate: () => void;
  currentTemplateId?: string | null;
  onUpdateTemplate: () => void;
  isUpdatingTemplate?: boolean; // Prop for update handler
  onDeleteTemplate?: (templateId: string) => void;
  onSetAsDefault?: (templateId: string) => void;
  isCurrentTemplateDefault?: boolean;
}

const DesignerHeader: React.FC<DesignerHeaderProps> = ({
  previewMode,
  onTogglePreview,
  showGrid,
  onToggleGrid,
  // selectedPageSize,
  // onPageSizeChange,
  // pageSizes,
  onUndo,
  onRedo,
  onClearCanvas,
  onGroup,
  selectedElements,
  availableTemplates,
  onTemplateSelect,
  onCreateNewTemplate,
  currentTemplateId,
  onUpdateTemplate,
  isUpdatingTemplate = false,
  onDeleteTemplate,
  onSetAsDefault,
  isCurrentTemplateDefault,
}) => {
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const handleClearCanvas = () => {
    setClearDialogOpen(false);
    onClearCanvas();
  };

  return (
    <>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h4">Template Designer</Typography>
            <Box sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
              <Tooltip title="Undo (⌘Z)">
                <span>
                  <IconButton
                    size="small"
                    sx={{ mx: 0.5 }}
                    onClick={onUndo}
                    disabled={!onUndo}
                  >
                    <Undo size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo (⌘⇧Z)">
                <span>
                  <IconButton
                    size="small"
                    sx={{ mx: 0.5 }}
                    onClick={onRedo}
                    disabled={!onRedo}
                  >
                    <Redo size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={showGrid ? 'Hide Grid' : 'Show Grid'}>
                <IconButton
                  size="small"
                  sx={{
                    mx: 0.5,
                    color: showGrid
                      ? colorPalette.redesign.lightPrimaryBlue
                      : 'inherit',
                  }}
                  onClick={onToggleGrid}
                >
                  <Grid size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear Canvas">
                <IconButton
                  size="small"
                  sx={{ mx: 0.5 }}
                  onClick={() => setClearDialogOpen(true)}
                >
                  <Eraser size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SliderToggle
              options={modeOptions}
              value={previewMode ? 'preview' : 'design'}
              onChange={(event, newValue) => {
                const wantsPreviewMode = newValue === 'preview';
                if (wantsPreviewMode !== previewMode) {
                  onTogglePreview();
                }
              }}
              data-qa="mode-slider-toggle"
              aria-label="View Mode"
              // size="small" // Adjust size as needed: 'small', 'medium', 'large'
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedElements.length > 1 && (
              <FBOButton
                variant="secondary"
                color="positive"
                icon="group"
                onClick={onGroup}
                sx={{ ml: 1 }}
                data-qa="group-button"
              >
                Group Selected Items
              </FBOButton>
            )}
            <FBOButton
              icon="plus"
              variant="primary"
              color="positive"
              sx={{ mx: 2 }}
              onClick={onCreateNewTemplate}
              data-qa="designer-header-create-new-template-button"
            >
              New
            </FBOButton>
            <FBOButton
              variant="secondary"
              color="positive"
              sx={{ mr: 2 }}
              onClick={onUpdateTemplate}
              disabled={!currentTemplateId || isUpdatingTemplate} // Disable if no template is selected or if updating
              loading={isUpdatingTemplate}
              data-qa="designer-header-update-template-button"
            >
              Update
            </FBOButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '300px',
              }}
            >
              <Autocomplete
                options={availableTemplates || []}
                getOptionLabel={(option) =>
                  `${option.name || 'Unnamed Template'}${
                    option.defaultTemplate ? ' (default)' : ''
                  }`
                }
                value={
                  availableTemplates.find(
                    (template) => template.id === currentTemplateId
                  ) || null
                }
                onChange={(event, newValue) => {
                  onTemplateSelect(newValue);
                }}
                sx={{ width: '300px' }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableClearable
                autoSelect={false}
              />
            </Box>
            <FBOMenuButton
              variant="secondary"
              items={generateTemplateMenuItems({
                currentTemplateId,
                isCurrentTemplateDefault,
                onSetAsDefault,
                onDeleteTemplate,
              })}
              disabled={!currentTemplateId}
              data-qa="template-options-menu"
              style={{
                color: colorPalette.redesign.contentSecondary,
                marginLeft: '8px', // Add some spacing
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        aria-labelledby="clear-canvas-dialog-title"
      >
        <DialogTitle id="clear-canvas-dialog-title">Clear Canvas</DialogTitle>
        <DialogContent>
          Are you sure you want to clear the canvas? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <FBOButton
            variant="tertiary"
            color="neutral"
            onClick={() => setClearDialogOpen(false)}
            data-qa="cancel-clear-button"
          >
            Cancel
          </FBOButton>
          <FBOButton
            variant="primary"
            color="negative"
            onClick={handleClearCanvas}
            data-qa="confirm-clear-button"
          >
            Clear Canvas
          </FBOButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DesignerHeader;
